/**
 * Приход клиента в салон в ленте «Чатов» — карточка клиента CRM (`sCustomer`): какая примерка
 * (первичная / повторная), салон, время и длительность, итог, стилист, ссылка на карточку в CRM,
 * что понравилось на примерке (сердечки, слайдером) и комментарии этого прихода.
 *
 * Просьба владельца 22.10.26 (чат 57880): в ленте было «Примерка завершена», а что за примерка и
 * что ей понравилось — нет. Сердечко — как карточка «Избранного» CRM (`FavoriteProductCard`): фото
 * модели (иначе серии), красное сердце у «понравилось», серия, цена проката.
 * Данные — `visits` ручек `GET /api/chats/{id}` и `…/history`; спека
 * `docs/specs/2026-10-22-chat-visit-card-design.md`.
 */
import { Heart, HeartOff, Search as SearchIcon, Store } from "lucide-react";
import { useState } from "react";

import { CommentBubble } from "@/components/chat/CommentBubble";
import type { OnOffer } from "@/components/chat/SerialPhotos";
import { SimilarDialog, type SimilarItem } from "@/components/chat/SimilarDialog";
import { type MediaItem, MediaViewer } from "@/components/MediaViewer";
import type { ChatComment, ChatHeart, ChatVisit } from "@/lib/clientChats";
import { NavMenu } from "@/nav/NavMenu";

const hhmm = (iso: string) => iso.slice(11, 16);
const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;

/** `sCustomer_type.typeShort` → словами. */
const TYPE: Record<string, string> = {
  Перв: "первичная",
  Повтор: "повторная",
  "Перв.БЗ": "без записи",
  Удаленка: "удалённая",
  Прием: "сдать платье",
  Партнер: "партнёр",
};
/** Итог примерки цветом: бронь — зелёным, «думать» — оранжевым, «ничего» — красным. */
const STATUS_TONE: Record<string, string> = {
  Сделка: "text-ok",
  "Ушел думать": "text-warn",
  "Думают(В шоуруме)": "text-warn",
  "Ничего не подошло": "text-err",
  "Не подошел наряд": "text-err",
};

function minutes(a: string, b: string | null): string | null {
  if (!b) return null;
  const m = Math.round((Date.parse(b) - Date.parse(a)) / 60000);
  if (!(m > 0)) return null;
  return m < 60 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60 ? `${m % 60} мин` : ""}`.trim();
}

const heartItem = (h: ChatHeart, v: ChatVisit): MediaItem => ({
  kind: "photo",
  url: h.photo ?? "",
  preview: h.photo,
  caption: [h.item, h.serial, h.price ? rub(h.price) : null].filter(Boolean).join(" · "),
  meta: [h.status, `примерка ${ddmm(v.date)}`, v.user, h.comment ? `«${h.comment}»` : null].filter(Boolean).join(" · "),
});

/** Сердечко → вещь окна похожих: ищем от серии, город — её салон. */
const similarItem = (h: ChatHeart, v: ChatVisit): SimilarItem => {
  const m = heartItem(h, v);
  return { key: h.id, photo: h.photo, of: h.serial ? { serial: h.serial } : null, title: h.item, lines: [m.caption ?? "", m.meta ?? ""] };
};

/** `comments` — комментарии этого прихода; `feed` — все комментарии ленты (их фото листаются вместе);
 * `onOffer` — отмеченные в окне похожих серии: фото с номером в поле ответа. */
export function VisitCard({
  v,
  comments,
  feed,
  onOffer,
}: {
  v: ChatVisit;
  comments: ChatComment[];
  feed: ChatComment[];
  onOffer: OnOffer;
}) {
  const [view, setView] = useState<number | null>(null);
  const [pick, setPick] = useState<number | null>(null);
  // Отметка кнопки статуса, совпадающая с итогом карточки, — повтор шапки.
  const own = comments.filter((c) => !(c.auto && c.text.trim().toLowerCase() === (v.status ?? "").toLowerCase()));
  const hearts = v.hearts.filter((h) => h.photo);
  const liked = hearts.filter((h) => h.like);
  const other = hearts.filter((h) => !h.like);
  const media = hearts.map((h) => heartItem(h, v));
  // Клик по сердечку: с серией — похожие по фото, как во вкладке «Товар»; без серии искать не от чего — слайдер.
  const open = (i: number) => (hearts[i]?.serial ? setPick(i) : setView(i));
  const long = minutes(v.date, v.finish);
  return (
    <div
      className="my-1 flex w-[min(94%,560px)] flex-none flex-col gap-1.5 self-center rounded-[14px] bg-[var(--tg-surface)] px-3 py-2 text-[13px] text-[var(--tg-text)] shadow-[var(--tg-shadow)]"
      data-annot="приход клиента в салон"
    >
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <Store size={15} className="flex-none text-[var(--tg-sec)]" />
        <span className="font-medium">Примерка{v.type ? ` ${TYPE[v.type] ?? v.type}` : ""}</span>
        {v.location && <span className="text-[var(--tg-sec)]">· {v.location}</span>}
        <span className="tabular-nums text-[var(--tg-sec)]">
          · {hhmm(v.date)}
          {v.finish && v.finish.slice(0, 10) === v.date.slice(0, 10) ? `–${hhmm(v.finish)}` : ""}
          {long ? ` (${long})` : ""}
        </span>
        <span className="ml-auto text-[12px]">
          <NavMenu r={{ obj: "customerID", id: v.id }} />
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-1.5 text-[12.5px]">
        {v.status && <span className={`font-medium ${STATUS_TONE[v.status] ?? (v.finished ? "" : "text-[var(--tg-sec)]")}`}>{v.status}</span>}
        {v.user && <span className="text-[var(--tg-sec)]">· стилист {v.user}</span>}
      </div>

      {liked.length > 0 && <Hearts title="Понравилось" list={liked} like media={hearts} onOpen={open} />}
      {other.length > 0 && <Hearts title="Не понравилось" list={other} like={false} media={hearts} onOpen={open} />}

      {own.length > 0 && (
        <div className="mt-0.5 border-t border-dashed border-[color-mix(in_srgb,var(--tg-sec)_35%,transparent)] pt-1.5">
          <CommentBubble comments={own} feed={feed} bare />
        </div>
      )}
      {view !== null && <MediaViewer items={media} start={view} onClose={() => setView(null)} />}
      {pick !== null && hearts[pick] && (
        <SimilarDialog
          list={hearts.map((h) => similarItem(h, v))}
          at={pick}
          location={null}
          onMove={setPick}
          onClose={() => setPick(null)}
          onOffer={onOffer}
        />
      )}
    </div>
  );
}

/** Ряд сердечек: фото с сердцем в углу, под фото — цена; клик — похожие по фото (без серии — слайдер). */
function Hearts({
  title,
  list,
  like,
  media,
  onOpen,
}: {
  title: string;
  list: ChatHeart[];
  like: boolean;
  media: ChatHeart[];
  onOpen: (i: number) => void;
}) {
  const Icon = like ? Heart : HeartOff;
  return (
    <div data-annot={like ? "сердечки — понравилось" : "сердечки — не понравилось"}>
      <div className="mb-1 flex items-center gap-1 text-[12px] text-[var(--tg-sec)]">
        <Icon size={13} className={like ? "fill-current text-err" : ""} />
        {title} · {list.length}
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-0.5">
        {list.map((h) => (
          <button
            key={h.id}
            type="button"
            onClick={() => onOpen(media.indexOf(h))}
            title={[[h.item, h.serial, h.status].filter(Boolean).join(" · "), h.serial ? "Нажмите — похожие по фото" : ""]
              .filter(Boolean)
              .join("\n")}
            className={`group relative w-[64px] flex-none text-left ${like ? "" : "opacity-60 hover:opacity-100"}`}
            data-annot="сердечко: найти похожие"
          >
            <img src={h.photo ?? ""} alt="" loading="lazy" className="h-[86px] w-[64px] rounded-md bg-black/10 object-cover" />
            {like && (
              <Heart size={14} className="absolute right-1 top-1 fill-current text-err drop-shadow-[0_0_2px_rgba(0,0,0,.5)]" />
            )}
            {h.serial && (
              <span className="absolute left-1 top-1 grid size-5 place-items-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <SearchIcon size={11} />
              </span>
            )}
            <span className="block truncate text-[11px] leading-tight text-[var(--tg-sec)]">{h.item ?? h.serial}</span>
            {h.price ? <span className="block text-[11px] font-medium leading-tight tabular-nums">{rub(h.price)}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
